import React, { useState } from 'react';
import { Button, Descriptions, Drawer, Grid, Typography, theme } from 'antd';
import { ExportOutlined } from '@ant-design/icons';

import { PointInTime } from '~components/PointInTime';

import { formatCtr, formatInspectionState, formatMetricCount, NO_VALUE } from './PagesFormatting';
import PagesHttpStatusValue from './PagesHttpStatusValue';
import PagesLastCrawledValue from './PagesLastCrawledValue';
import type { PagesRow } from './PagesTable';

const PAGES_INSPECT_DRAWER_STRINGS = {
  title: 'Inspect URL',
  openInNewTab: 'Open in new tab',
  prerenderSection: 'Prerender',
  searchConsoleSection: 'Search Console',
  lastCrawled: 'Last crawled',
  httpStatus: 'HTTP Status',
  impressions: 'Impressions',
  clicks: 'Clicks',
  ctrPercent: 'CTR%',
  indexStatus: 'Index status',
  inspectionDate: 'Inspection date',
};

const DRAWER_WIDTH = 480; // FIXME: no exact token

export type PagesInspectDrawerProps = {
  /** `null` closes the drawer. The last row is held so antd can play the close animation. */
  row: PagesRow | null;
  onClose: () => void;
  showInspection?: boolean;
};

function PagesInspectDrawer({ row, onClose, showInspection = false }: PagesInspectDrawerProps) {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const [heldRow, setHeldRow] = useState(row);
  if (row !== null && row !== heldRow) {
    setHeldRow(row);
  }
  const view = row ?? heldRow;

  return (
    <Drawer
      open={row !== null}
      onClose={onClose}
      title={PAGES_INSPECT_DRAWER_STRINGS.title}
      // Unresolved breakpoints (`{}`) fall through to the desktop width — only
      // a confirmed narrow viewport goes full-width.
      width={screens.xs ? '100%' : DRAWER_WIDTH}
      extra={
        view ? (
          <Button size="small" href={view.url} target="_blank" rel="noopener noreferrer" icon={<ExportOutlined />}>
            {PAGES_INSPECT_DRAWER_STRINGS.openInNewTab}
          </Button>
        ) : undefined
      }
    >
      {view && (
        <>
          <Typography.Paragraph
            copyable={{ text: view.url }}
            style={{ fontSize: token.fontSizeSM, wordBreak: 'break-all' }}
          >
            {view.url}
          </Typography.Paragraph>

          <Descriptions
            title={PAGES_INSPECT_DRAWER_STRINGS.prerenderSection}
            column={1}
            size="small"
            bordered
            style={{ marginBottom: token.marginLG }}
            items={[
              {
                key: 'lastCrawled',
                label: PAGES_INSPECT_DRAWER_STRINGS.lastCrawled,
                children: (
                  <PagesLastCrawledValue lastCrawledAt={view.lastCrawledAt} lastStatusCode={view.lastStatusCode} />
                ),
              },
              {
                key: 'httpStatus',
                label: PAGES_INSPECT_DRAWER_STRINGS.httpStatus,
                children: <PagesHttpStatusValue statusCode={view.lastStatusCode} lastCrawledAt={view.lastCrawledAt} />,
              },
            ]}
          />

          <Descriptions
            title={PAGES_INSPECT_DRAWER_STRINGS.searchConsoleSection}
            column={1}
            size="small"
            bordered
            items={[
              {
                key: 'impressions',
                label: PAGES_INSPECT_DRAWER_STRINGS.impressions,
                children: formatMetricCount(view.impressions, view.metricState),
              },
              {
                key: 'clicks',
                label: PAGES_INSPECT_DRAWER_STRINGS.clicks,
                children: formatMetricCount(view.clicks, view.metricState),
              },
              {
                key: 'ctr',
                label: PAGES_INSPECT_DRAWER_STRINGS.ctrPercent,
                children: formatCtr(view.ctrPct),
              },
              ...(showInspection
                ? [
                    {
                      key: 'indexStatus',
                      label: PAGES_INSPECT_DRAWER_STRINGS.indexStatus,
                      children: formatInspectionState(view),
                    },
                    {
                      key: 'inspectionDate',
                      label: PAGES_INSPECT_DRAWER_STRINGS.inspectionDate,
                      children: view.inspectedAt === null ? NO_VALUE : <PointInTime value={view.inspectedAt} />,
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}
    </Drawer>
  );
}

export default PagesInspectDrawer;
